import { Phone, Mail, Clock, MapPin, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { companyInfo } from "../data/mock";

export const Footer = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="bg-slate-900 text-slate-300">
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-800 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">O</span>
              </div>
              <h3 className="text-xl font-bold text-white">{companyInfo.name}</h3>
            </div>
            <p className="text-sm leading-relaxed">
              Professional car transport and recovery services across the UK. Available 24/7 for all your vehicle transportation needs.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="hover:text-white transition-colors"
                >
                  Our Services
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('how-it-works')}
                  className="hover:text-white transition-colors"
                >
                  How It Works
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('coverage')}
                  className="hover:text-white transition-colors"
                >
                  Coverage Areas
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('testimonials')}
                  className="hover:text-white transition-colors"
                >
                  Testimonials
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('quote')}
                  className="hover:text-white transition-colors"
                >
                  Get a Quote
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold mb-4">Recovery Services</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/services/breakdown-recovery" className="hover:text-white transition-colors">
                  Breakdown Recovery
                </Link>
              </li>
              <li>
                <Link to="/services/vehicle-transport" className="hover:text-white transition-colors">
                  Vehicle Transport
                </Link>
              </li>
              <li>
                <Link to="/services/auction-transport" className="hover:text-white transition-colors">
                  Auction Transport
                </Link>
              </li>
              <li>
                <Link to="/services/jump-start" className="hover:text-white transition-colors">
                  Jump Start Service
                </Link>
              </li>
              <li>
                <Link to="/services/emergency-recovery" className="hover:text-white transition-colors">
                  Emergency Recovery
                </Link>
              </li>
            </ul>
          </div>

          {/* Additional Services */}
          <div>
            <h4 className="text-white font-bold mb-4">Other Services</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/services/vehicle-disposal" className="hover:text-white transition-colors">
                  Vehicle Disposal
                </Link>
              </li>
              <li>
                <Link to="/services/parts-sales" className="hover:text-white transition-colors">
                  Used Parts Sales
                </Link>
              </li>
              <li>
                <Link to="/services/mobile-mechanic" className="hover:text-white transition-colors">
                  Mobile Mechanic
                </Link>
              </li>
              <li>
                <Link to="/services/vehicle-servicing" className="hover:text-white transition-colors">
                  Vehicle Servicing
                </Link>
              </li>
              <li>
                <Link to="/blog" className="hover:text-white transition-colors">
                  Blog & News
                </Link>
              </li>
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h4 className="text-white font-bold mb-4">Coverage Areas</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/locations/hackney" className="hover:text-white transition-colors">
                  Hackney
                </Link>
              </li>
              <li>
                <Link to="/locations/shoreditch" className="hover:text-white transition-colors">
                  Shoreditch
                </Link>
              </li>
              <li>
                <Link to="/locations/stratford" className="hover:text-white transition-colors">
                  Stratford
                </Link>
              </li>
              <li>
                <Link to="/locations/islington" className="hover:text-white transition-colors">
                  Islington
                </Link>
              </li>
              <li>
                <Link to="/locations/tower-hamlets" className="hover:text-white transition-colors">
                  Tower Hamlets
                </Link>
              </li>
              <li>
                <Link to="/locations/bethnal-green" className="hover:text-white transition-colors">
                  Bethnal Green
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold mb-4">Contact Us</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <Phone className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" />
                <div>
                  <p className="text-white font-semibold">24/7 Emergency</p>
                  <a href={`tel:${companyInfo.emergencyPhone}`} className="hover:text-white transition-colors">
                    {companyInfo.emergencyPhone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <MessageCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-500" />
                <div>
                  <p className="text-white font-semibold">WhatsApp</p>
                  <a 
                    href={`https://wa.me/${companyInfo.whatsappNumber}?text=Hi, I need a quote`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors"
                  >
                    {companyInfo.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <a href={`mailto:${companyInfo.email}`} className="hover:text-white transition-colors">
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{companyInfo.hours}</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{companyInfo.address}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-800 pt-8 mt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
            <p>© 2024 {companyInfo.name}. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms & Conditions</a>
              <a href="#" className="hover:text-white transition-colors">Cookie Policy</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
